"use client";

import React from "react";
import { Ticket, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Link from "next/link";
import { useTicket } from "@/lib/hooks/api/tickets";
import { LinkedEntityContextShell } from "./LinkedEntityContextShell";
import {
  getLinkedEntityFullPageHref,
  getTaskLinkedEntityId,
  normalizeTaskLinkedEntityType,
} from "@/lib/utils/crm/task-linked-entity";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { Contact_Tasks } from "@/src/gql/graphql";

/**
 * Linked ticket
 */
export function TaskLinkedTicketPanel({ item }: TabContentProps<Contact_Tasks>) {
  const ticketId = getTaskLinkedEntityId(item);
  const enabled = normalizeTaskLinkedEntityType(item.entity_type) === "tickets" && !!ticketId;
  const { data: ticket, isLoading, error } = useTicket(ticketId ?? "", { enabled });

  if (!enabled) return null;

  return (
    <LinkedEntityContextShell task={item} expectedKind="tickets">
      {isLoading ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            <Ticket className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Loading ticket…</p>
          </CardContent>
        </Card>
      ) : error || !ticket ? (
        <Card>
          <CardContent className="py-8 text-center text-red-600 dark:text-red-400">
            <p>Could not load this ticket.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="w-full space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Ticket className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <h3 className="text-lg font-semibold text-card-foreground">Ticket</h3>
            </div>
            <Button asChild variant="outline" size="sm" className="gap-2">
              <Link href={getLinkedEntityFullPageHref("tickets", ticket.id)}>
                <ExternalLink className="w-4 h-4" />
                Open ticket
              </Link>
            </Button>
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{(ticket as { subject?: string }).subject ?? "Ticket"}</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <div>
                <span className="text-gray-600 dark:text-gray-400">Status: </span>
                <span className="capitalize">{(ticket as { status?: string }).status ?? "—"}</span>
              </div>
              {(ticket as { ticket_number?: number }).ticket_number != null ? (
                <div>
                  <span className="text-gray-600 dark:text-gray-400">#: </span>
                  <span>{(ticket as { ticket_number?: number }).ticket_number}</span>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      )}
    </LinkedEntityContextShell>
  );
}
